import React, { useState, useEffect } from 'react'
import { ReactComponent as TriangleWarningIcon } from '../../../assets/svg/TriangleWarningIcon.svg'
import { ReactComponent as OnePerson } from '../../../assets/svg/OnePerson.svg'
import { ReactComponent as ManyPersons } from '../../../assets/svg/ManyPersons.svg'
import { ReactComponent as ConfirmIcon } from '../../../assets/svg/ConfirmIcon.svg'
import { ReactComponent as ChevronDownDuoIcon } from '../../../assets/svg/ChevronDownDuoIcon.svg'
import { ReactComponent as InfoIconWithRound } from '../../../assets/svg/InfoIconWithRound.svg'
import { format } from 'date-fns'
import { emotions_heads } from '../../../constants/Constants'
import { ReactComponent as HeartSmallIcon } from '../../../assets/svg/HeartSmallIcon.svg'
import { ReactComponent as AddPlusCircleSmallIcon } from '../../../assets/svg/AddPlusCircleSmallIcon.svg'
import { classAlerts, studentAlerts } from '../../../constants/StudentAnalyticsConstants'
import { useMemo } from 'react'
import { LoadingContainer } from './CheckInWidget'
import { format_day, formatDate, useHasStudentInPath, useQuery } from '../../../utils/util'
import { loadClassAlertsAsync, loadStudentAlertsAsync, seenAlert } from '../../../server/server'
import WellBeingAsignModal from '../../modals/WellBeingAsignModal'
import WellBeingTakeActionModal from '../../modals/WellBeingTakeActionModal'
import ExpandButton from '../../common/ExpandButton'
import { AlertActivity, SingleEmotionDetails } from './AlertDetails'
import i18n from '../../../i18n'
import { useTranslation } from 'react-i18next'
import { useUser } from '../../../auth'
import InfoButton from '../../common/InfoButton'
import { useTourContext } from '../../../context/TourContext'
import { ReactComponent as FaceSadIcon } from '../../../assets/svg/FaceSadIcon.svg'
import AssignFilter from '../../alerts/alerts/AssignFilter'
import { alertsFilterList, alertsListMap } from '../../alerts/constants/AlertsConstants'

export { AlertActivity, SingleEmotionDetails }

const SHOW_ROWS = 3

function AlertsWidget({ daysFilter, student }) {
    const { t } = useTranslation()
    const query = useQuery()
    const isStudentAnalytics = useHasStudentInPath()
    const currentId = query.get('id')
    const [limit, setLimit] = useState(SHOW_ROWS)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(studentAlerts)
    const [noData, setNoData] = useState(false)
    const [selectedFilter, setSelectedFilter] = useState(alertsFilterList[0])

    const showLoading = useMemo(() => loading || noData, [loading, noData])
    const { tourOpen } = useTourContext()

    useEffect(() => {
        setLoading(true)

        loadData()
    }, [daysFilter, currentId])

    const loadData = async (alertType = selectedFilter) => {
        const callFunction = isStudentAnalytics ? loadStudentAlertsAsync : loadClassAlertsAsync
        const resp = await callFunction(currentId, daysFilter.startDate, daysFilter.endDate, alertType)
        if (resp.status === 200) {
            setNoData(!resp?.data?.length)
            setData(!!resp?.data?.length ? resp.data : studentAlerts)
            console.log('alerts', resp.data)
        } else {
            setData(studentAlerts)
            setNoData(true)
        }
        setLoading(false)
    }

    const selectAssignedFilter = (filter) => {
        setLoading(true)
        setSelectedFilter(filter)
        loadData(filter)
    }

    return (
        <div className="bg-white rounded-[24px] p-6 mt-4" data-tour="alerts">
            <div className=" flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{t('Alerts')}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px]">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray ">{t(daysFilter.label)}</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <button>
                        <InfoButton
                            color="#5E639B"
                            text={
                                isStudentAnalytics
                                    ? t(
                                          'This section displays alerts created for the student based on logged emotions or activities. You can view details, reasons, and the assigned status of each alert, and take or assign actions as needed.'
                                      )
                                    : t(
                                          'This section shows alerts for students who have logged 3 consecutive uncomfortable emotions. You can use these alerts to monitor your students’ emotional wellbeing and take necessary actions.'
                                      )
                            }
                        />
                    </button>
                    <AssignFilter selectedFilter={selectedFilter} setSelectedFilter={selectAssignedFilter} list={alertsFilterList} contentListMap={alertsListMap} width={215} />
                </div>
            </div>
            <div className="relative mt-6">
                <DataContainer
                    data={data}
                    blur={showLoading}
                    maxSize={limit}
                    student={student}
                    loadData={loadData}
                    limit={limit}
                    setLimit={setLimit}
                    isStudentAnalytics={isStudentAnalytics}
                />
                {showLoading && !tourOpen && <LoadingContainer showEmptyData={noData} />}
            </div>
        </div>
    )
}

export default AlertsWidget

const DataContainer = ({ data, blur, maxSize, student, loadData = () => {}, limit, setLimit, isStudentAnalytics }) => {
    const { t } = useTranslation()
    const { user } = useUser()
    const [selectedFilter, setSelectedFilter] = useState(() => (e) => true)
    const [selectedButton, setSelectedButton] = useState('')
    const { tourOpen } = useTourContext()
    const [showAssignActionModal, setShowAssignActionModal] = useState(false)
    const [showTakeActionModal, setShowTakeActionModal] = useState(false)
    const [selectedNote, setSelectedNote] = useState(null)

    const { notAssigned, assignedToMe, assignedToPerson, completed } = useMemo(() => {
        let notAssigned = 0
        let assignedToMe = 0
        let assignedToPerson = 0
        let completed = 0
        data?.forEach((item) => {
            if (item?.activityHistory?.some((e) => e.type === 'actionTaken')) completed++
            else if (item?.activityHistory?.some((e) => e.type === 'assignedAction')) {
                const index = item?.activityHistory?.findIndex((e) => e.type === 'assignedAction' && e.assignedTo.id === user.id)
                if (index !== -1) assignedToMe++
                else return assignedToPerson++
            } else notAssigned++
        })
        return { notAssigned, assignedToMe, assignedToPerson, completed }
    }, [data, user])

    const handleShowMore = () => {
        if (limit === SHOW_ROWS) {
            setLimit(data?.length)
        } else {
            setLimit(SHOW_ROWS)
        }
    }

    const selectFilter = (filter) => {
        if (filter == selectedButton) {
            setSelectedFilter(() => (e) => true)
            setSelectedButton('')
            return
        }
        switch (filter) {
            case 'completed':
                setSelectedFilter(() => (e) => e?.activityHistory?.some((e) => e.type === 'actionTaken'))
                break
            case 'notAssigned':
                setSelectedFilter(() => (e) => !e?.activityHistory?.some((e) => e.type === 'assignedAction'))
                break
            case 'assignedToMe':
                setSelectedFilter(
                    () => (e) =>
                        e?.activityHistory?.some((e) => e.type === 'assignedAction' && e.assignedTo.id === user.id) && !e?.activityHistory?.some((e) => e.type === 'actionTaken')
                )
                break
            case 'assignedToPerson':
                setSelectedFilter(
                    () => (e) =>
                        e?.activityHistory?.some((e) => e.type === 'assignedAction' && e.assignedTo.id !== user.id) && !e?.activityHistory?.some((e) => e.type === 'actionTaken')
                )
                break
            default:
                break
        }
        setSelectedButton(filter)
    }

    const dataArray = useMemo(() => {
        return data.filter(selectedFilter)
    }, [data, selectedFilter])

    const openAssignActionModal = (note) => {
        setSelectedNote(note)
        setShowAssignActionModal(true)
    }
    const closeAssignActionModal = (success) => {
        if (success) loadData()
        setShowAssignActionModal(false)
    }

    const openTakeActionModal = (note) => {
        setSelectedNote(note)
        setShowTakeActionModal(true)
    }
    const closeTakeActionModal = (success) => {
        if (success) loadData()
        setShowTakeActionModal(false)
    }

    return (
        <div className={`flex flex-col gap-6 ${blur && !tourOpen ? 'blur' : ''}`}>
            <div className="flex flex-row items-center gap-6">
                {!!notAssigned && (
                    <button
                        className={`flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#FC4C5966] rounded-full border transition ${
                            selectedButton === 'notAssigned' ? 'border-[#FC4C59]' : 'border-[transparent]'
                        }`}
                        onClick={() => selectFilter('notAssigned')}
                    >
                        <TriangleWarningIcon />
                        <p className="text-[16px] leading-[20px] font-medium text-darkenPurple ">
                            {notAssigned} {t('Not assigned')}
                        </p>
                    </button>
                )}
                {!!assignedToMe && (
                    <button
                        className={`flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#ECECF2] rounded-full border transition ${
                            selectedButton === 'assignedToMe' ? 'border-[#77768D]' : 'border-[transparent]'
                        }`}
                        onClick={() => selectFilter('assignedToMe')}
                    >
                        <OnePerson />
                        <p className="text-[16px] leading-[20px] font-medium text-[#77768D] ">
                            <span className="text-darkenPurple ">{assignedToMe}</span> {t('Assigned to me')}
                        </p>
                    </button>
                )}
                {!!assignedToPerson && (
                    <button
                        className={`flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#ECECF2] rounded-full border transition ${
                            selectedButton === 'assignedToPerson' ? 'border-[#77768D]' : 'border-[transparent]'
                        }`}
                        onClick={() => selectFilter('assignedToPerson')}
                    >
                        <ManyPersons />
                        <p className="text-[16px] leading-[20px] font-medium text-[#77768D]  ">
                            <span className="text-darkenPurple ">{assignedToPerson}</span> {t('Assigned to others')}
                        </p>
                    </button>
                )}
                {!!completed && (
                    <button
                        className={`flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#6FCC874D] rounded-full border transition ${
                            selectedButton === 'completed' ? 'border-[#2B793F]' : 'border-[transparent]'
                        }`}
                        onClick={() => selectFilter('completed')}
                    >
                        <ConfirmIcon />
                        <p className="text-[16px] leading-[20px] font-medium text-[#2B793FDE]  ">
                            {completed} {t('Completed')}
                        </p>
                    </button>
                )}
            </div>
            <div className={` min-h-[132px] flex flex-col gap-[4px] `}>
                {dataArray?.slice(0, maxSize)?.map((item, index) => (
                    <SingleAlertItem
                        item={item}
                        key={item?.id ?? index}
                        student={student}
                        loadData={loadData}
                        isStudentAnalytics={isStudentAnalytics}
                        initialOpen={index === 0 && tourOpen}
                        openTakeActionModal={openTakeActionModal}
                        openAssignActionModal={openAssignActionModal}
                    />
                ))}
            </div>
            <div className="flex flex-row justify-end w-full ">
                {!blur && dataArray?.length > SHOW_ROWS && (
                    <button
                        className="flex flex-row items-center justify-center gap-2 border border-[#ECEBF8] hover:border-darkenPurple transition rounded-full h-[34px] px-4 bg-[#F8F8FB]"
                        onClick={() => handleShowMore()}
                        disabled={blur || (!blur && !data)}
                    >
                        <p className="text-darkenPurple text-[16px] leading-5 font-semibold">{limit > SHOW_ROWS ? t('Show less') : t('Show more')}</p>
                        <div className={`${limit > SHOW_ROWS ? 'rotate-180' : 'rotate-0'} transition`}>
                            <ChevronDownDuoIcon />
                        </div>
                    </button>
                )}
            </div>
            <WellBeingAsignModal
                modalIsOpen={showAssignActionModal}
                closeModal={closeAssignActionModal}
                student={student ?? selectedNote?.student}
                note={selectedNote?.feedback ?? selectedNote}
                multipleEmotions={selectedNote?.type === 'multipleNegativeEmotions'}
                isAlert
            />
            <WellBeingTakeActionModal
                modalIsOpen={showTakeActionModal}
                closeModal={closeTakeActionModal}
                student={student ?? selectedNote?.student}
                note={selectedNote?.feedback ?? selectedNote}
                multipleEmotions={selectedNote?.type === 'multipleNegativeEmotions'}
                isAlert
            />
        </div>
    )
}

export const SingleAlertItem = ({ item, student, loadData = () => {}, isStudentAnalytics, initialOpen, openAssignActionModal, openTakeActionModal }) => {
    const { t } = useTranslation()
    const { user } = useUser()
    const [open, setOpen] = useState(initialOpen)

    const [itemState, assignedToName] = useMemo(() => {
        if (item?.activityHistory?.some((e) => e.type === 'actionTaken')) return ['completed', '']
        else if (item?.activityHistory?.some((e) => e.type === 'assignedAction')) {
            const index = item?.activityHistory?.findIndex((e) => e.type === 'assignedAction' && e.assignedTo.id === user.id)
            if (index !== -1) return ['assignedToMe', '']
            else return ['assignedToPerson', `${item?.activityHistory[index]?.assignedTo?.first_name ?? ''} ${item?.activityHistory[index]?.assignedTo?.last_name ?? ''}`]
        }
        return ['notAssigned', '']
    }, [item, user])

    useEffect(() => {
        setOpen(initialOpen)
    }, [initialOpen])

    useEffect(() => {
        if (open) {
            seenItem()
        } else {
            // closeTakeActionModal()
            // closeAssignActionModal()
        }
    }, [open])

    const seenItem = async () => {
        await seenAlert(item.id)
    }

    const getButton = (state, assignedToName) => {
        switch (state) {
            case 'notAssigned':
                return <NotAssignedButton />
            case 'assignedToMe':
                return <AssignedToMeButton />
            case 'assignedToPerson':
                return <AssignedToPersonButton name={assignedToName} />
            case 'completed':
                return <CompletedButton />
            default:
                return <NotAssignedButton />
        }
    }

    const isMultipleEmotions = item?.type === 'multipleNegativeEmotions'

    return (
        <div className="bg-[#F8F8FB] w-full rounded-[16px] p-3" data-tour="single_item">
            <div className="flex flex-row items-center pointer" onClick={() => setOpen((prev) => !prev)}>
                <div
                    className="flex items-center justify-center rounded-full w-10 h-10 "
                    style={{ backgroundColor: itemState == 'completed' ? '#6FCC874D' : isMultipleEmotions ? '#413F5E1A' : '#FC4C5933' }}
                >
                    {itemState == 'completed' ? (
                        <ConfirmIcon width={24} height={24} />
                    ) : isMultipleEmotions ? (
                        <FaceSadIcon width={24} height={24} />
                    ) : (
                        <TriangleWarningIcon width={24} height={24} />
                    )}
                </div>
                <p className="text-darkenPurple text-[14px] leading-5 font-semibold ml-[10px]">
                    {isMultipleEmotions
                        ? isStudentAnalytics
                            ? t('Multiple Uncomfortable Emotions')
                            : t('John Doe has logged 3 uncomfortable emotions on', {
                                  first_name: item.user?.first_name ?? '',
                                  last_name: item.user?.last_name ?? '',
                                  number: item.feedbacks?.length ?? 0,
                              })
                        : isStudentAnalytics
                        ? t('Keyword Alert')
                        : `${item.user?.first_name ?? ''} ${item.user?.last_name ?? ''}`}
                    {' - '}
                </p>
                {isMultipleEmotions ? (
                    <div className="flex flex-row items-center ml-[5px]">
                        {item?.feedbacks?.map((feedback, index) => (
                            <img key={index} src={emotions_heads[feedback.emotion]} width={24} height={24} alt="emotion" style={{ marginLeft: index === 0 ? 0 : -8 }} />
                        ))}
                    </div>
                ) : (
                    <p className="text-[#FC4C59] text-[14px] leading-5 font-bold ml-[5px]">{t(item?.category_name)}</p>
                )}
                <p className="text-[#413F5E] text-[14px] font-medium flex-1 ml-[5px]">
                    {' - '}
                    {format(new Date(item?.created_date), 'dd.MM.yyyy.')}
                </p>
                <div className="mr-2">{getButton(itemState)}</div>
                <ExpandButton open={open} onClick={() => {}} />
            </div>
            <div className={`overflow-hidden transition-all ${open ? 'max-h-[1000px] h-auto' : 'max-h-0'} ${open ? 'opacity-100' : 'opacity-0'}`}>
                <div className="w-full h-[1px] bg-[#ECEBF8] mt-[10px]"></div>
                {!isMultipleEmotions && (
                    <div className="flex flex-col gap-[6px] flex-1 bg-[#FFF5F6] rounded-[8px] pt-2 pr-6 pb-3 pl-5  mt-3">
                        <div className=" flex flex-row items-center justify-between">
                            <div className="flex flex-row items-center gap-2 ">
                                <div className="min-w-7 max-w-7 h-7 rounded-full bg-[#B8B8D6] flex items-center justify-center">
                                    <p className="text-[12px] leading-[20px] font-medium text-[#000000] ">
                                        {item.user?.first_name[0]}
                                        {item.user?.last_name[0]}
                                    </p>
                                </div>
                                <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">
                                    {item.user?.first_name} {item.user?.last_name} <span className="font-medium"> {t('included in their response')}: </span>
                                </p>
                            </div>
                            <p className="text-darkenPurple text-[12px] leading-5 font-medium">{format(new Date(item?.created_date), 'dd.MM.yyyy HH:mm')}</p>
                        </div>
                        {<p className="text-darkenPurple text-[14px] leading-[22px] font-medium ml-9">{item?.feedback?.message}</p>}
                    </div>
                )}
                <>
                    <div className="flex flex-col gap-[10px] mt-2">
                        {item?.feedbacks?.map((emotion) => (
                            <SingleEmotionDetails emotion={emotion} student={student ?? item?.student} key={emotion.id} />
                        ))}
                    </div>

                    <div className="w-full border-t border-t-[#ECEBF8] mt-[12px]  flex flex-col pt-[12px] gap-1">
                        {item?.activityHistory?.map((activity, index) => (
                            <AlertActivity activity={activity} key={activity?.id ?? index} />
                        ))}
                    </div>

                    <div className="flex flex-row justify-end w-full" data-tour="button">
                        <button
                            className="h-[36px] bg-[#86D3FF33] rounded-full flex flex-row items-center justify-center border border-[#86D3FF33] hover:border-darkenPurple transition gap-1 px-4"
                            onClick={() => openTakeActionModal(item)}
                        >
                            <HeartSmallIcon /> <p className="text-darkenPurple text-[14px] leading-5 font-normal">{t('Take Action')}</p>
                        </button>
                        <button
                            className="h-[36px] bg-[#86D3FF33] rounded-full flex flex-row items-center justify-center border border-[#86D3FF33] hover:border-darkenPurple transition gap-1 px-4 ml-[10px]"
                            onClick={() => openAssignActionModal(item)}
                        >
                            <AddPlusCircleSmallIcon />
                            <p className="text-darkenPurple text-[14px] leading-5 font-normal">{t('Assign Action')}</p>
                        </button>
                    </div>
                </>
            </div>
        </div>
    )
}

const NotAssignedButton = () => {
    const { t } = useTranslation()
    return (
        <button className="flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#FC4C5966] rounded-full">
            <TriangleWarningIcon />
            <p className="text-[14px] leading-[18px] font-medium text-darkenPurple whitespace-nowrap">{t('Not assigned')}</p>
        </button>
    )
}
const AssignedToMeButton = () => {
    const { t } = useTranslation()
    return (
        <button className="flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#ECECF2] rounded-full">
            <InfoIconWithRound />
            <p className="text-[14px] leading-[18px] font-medium text-[#77768D] whitespace-nowrap">{t('Assigned to me')}</p>
        </button>
    )
}
const AssignedToPersonButton = (assignedToName) => {
    const { t } = useTranslation()
    return (
        <button className="flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#ECECF2] rounded-full">
            <InfoIconWithRound />
            <p className="text-[14px] leading-[18px] font-medium text-[#77768D] whitespace-nowrap ">{t('Assigned to others', { name: 'assignedToName' })}</p>
        </button>
    )
}
const CompletedButton = () => {
    const { t } = useTranslation()
    return (
        <button className="flex flex-row items-center gap-[6px] pl-1 pr-[10px] py-[5px] bg-[#6FCC874D] rounded-full">
            <ConfirmIcon />
            <p className="text-[14px] leading-[18px] font-medium text-[#2B793FDE]  ">{t('Completed')}</p>
        </button>
    )
}
